import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ShieldCheck, Mail, Lock, Download, FileText, FileArchive, ArrowDownToLine } from 'lucide-react';
import { LanguageProvider, useLang } from '../contexts/LanguageContext';
import { Footer } from '../components/landing/Footer';
import { ThemeToggle } from '../components/ThemeToggle';

const DPA_MAILTO =
  'mailto:info@kaizenkanri.nl' +
  '?subject=' + encodeURIComponent('Kanriva\u2122 — DPA & security pack request') +
  '&body=' + encodeURIComponent(
    'Hello,\n\nPlease send the data processing agreement and security pack for Kanriva.\n\n' +
    '• Company:\n• Contact person & role:\n• Number of employees in scope:\n\nThanks!'
  );

const SecurityBody = () => {
  const { t } = useLang('security');

  return (
    <div className="lp-scope min-h-screen bg-[#050a14] text-white">
      <header className="border-b border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="security-home-link">
            <img src="/brand/kanriva-logo.png" alt="Kanriva" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button asChild variant="ghost" className="text-white/60 hover:text-white" data-testid="security-back">
              <Link to="/"><ArrowLeft className="h-4 w-4 mr-2" /> {t.backHome}</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#00CED1]/40 bg-[#00CED1]/10 text-[#00CED1] text-xs font-semibold mb-5">
            <ShieldCheck className="h-3.5 w-3.5" /> {t.eyebrow}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-5">{t.title}</h1>
          <p className="text-lg text-white/60 leading-relaxed">{t.lead}</p>
          <p className="mt-4 text-xs uppercase tracking-wider text-white/30">{t.updated}</p>
        </div>

        <div className="mt-10 p-5 rounded-2xl bg-[#00CED1]/[0.07] border border-[#00CED1]/20 flex items-start gap-3" data-testid="security-summary">
          <Lock className="h-5 w-5 text-[#00CED1] mt-0.5 shrink-0" />
          <p className="text-sm text-white/75 leading-relaxed">{t.summaryLine}</p>
        </div>

        <div className="mt-12 space-y-4" data-testid="security-groups">
          {(t.groups || []).map((g, i) => (
            <section
              key={g.title}
              className="p-7 rounded-2xl bg-white/[0.02] border border-white/10"
              data-testid={`security-group-${i}`}
            >
              <h2 className="text-lg md:text-lg font-semibold text-white mb-4">{g.title}</h2>
              <ul className="space-y-3">
                {(g.items || []).map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-white/60 leading-relaxed">
                    <span className="h-1.5 w-1.5 mt-2 rounded-full bg-[#00CED1] shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-14" data-testid="security-docs">
          <div className="flex items-center gap-2 mb-2">
            <Download className="h-4 w-4 text-[#00CED1]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">{t.docsEyebrow}</span>
          </div>
          <h2 className="text-2xl font-bold mb-3">{t.docsTitle}</h2>
          <p className="text-sm text-white/55 leading-relaxed max-w-3xl mb-7">{t.docsLead}</p>

          {t.docsPack && (
            <a
              href={`/legal/${t.docsPack.file}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group block p-6 rounded-2xl bg-[#00CED1]/[0.09] border border-[#00CED1]/30 hover:border-[#00CED1]/60 transition-colors mb-4"
              data-testid="security-doc-pack"
            >
              <div className="flex items-start gap-4">
                <span className="h-10 w-10 rounded-xl bg-[#00CED1]/15 border border-[#00CED1]/30 flex items-center justify-center shrink-0">
                  <FileArchive className="h-5 w-5 text-[#00CED1]" />
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-white group-hover:text-[#00CED1] transition-colors">
                    {t.docsPack.name} <ArrowDownToLine className="inline h-4 w-4 ml-1 -mt-1" />
                  </p>
                  <p className="text-sm text-white/55 leading-relaxed mt-1">{t.docsPack.sub}</p>
                </div>
              </div>
            </a>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {(t.docs || []).map((d, i) => (
              <a
                key={d.file}
                href={`/legal/${d.file}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-[#00CED1]/40 transition-colors"
                data-testid={`security-doc-${i}`}
              >
                <div className="flex items-start gap-4">
                  <span className="h-10 w-10 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-[#00CED1]" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-white group-hover:text-[#00CED1] transition-colors">
                      {d.name} <ArrowDownToLine className="inline h-4 w-4 ml-1 -mt-1" />
                    </p>
                    <p className="text-sm text-white/50 leading-relaxed mt-1">{d.sub}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
          <p className="text-xs text-white/35 leading-relaxed mt-5 max-w-3xl">{t.docsNote}</p>
        </div>

        <div className="mt-12 p-7 rounded-2xl bg-gradient-to-r from-[#00CED1]/12 to-transparent border border-white/10">
          <h2 className="text-xl font-semibold mb-2">{t.cta}</h2>
          <p className="text-sm text-white/55 leading-relaxed mb-5 max-w-2xl">{t.ctaSub}</p>
          <Button asChild className="bg-[#00CED1] hover:bg-[#00b4b7] text-[#0a0a0a] font-semibold" data-testid="security-dpa-cta">
            <a href={DPA_MAILTO}><Mail className="h-4 w-4 mr-2" /> info@kaizenkanri.nl</a>
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default function SecurityPage() {
  return (
    <LanguageProvider>
      <SecurityBody />
    </LanguageProvider>
  );
}
